import AddIcon from "@atlaskit/icon/core/add";
import CommentIcon from "@atlaskit/icon/core/comment";
import LinkExternalIcon from "@atlaskit/icon/core/link-external";
import OnCallIcon from "@atlaskit/icon/core/on-call";
import RoadmapIcon from "@atlaskit/icon/core/roadmap";
import ShowMoreHorizontalIcon from "@atlaskit/icon/core/show-more-horizontal";
import {ScheduleCallIcon} from "~/components/svgs";
import {WhatsappIcon} from "~/components/svgs";
import SplitParallelIcon from "@atlaskit/icon-lab/core/split-parallel";
import {token} from "@atlaskit/tokens";
import {
    useState,
    type CSSProperties,
    type Dispatch,
    type MouseEvent,
    type PointerEvent,
    type ReactNode,
    type Ref,
    type SetStateAction,
} from "react";
import {formatDate, formatDateTime} from "~/utilities/utilities";
import {useCheckMobile} from "~/hooks/useCheckMobile";
import {AnankeCheckbox} from "@vesta/ananke/components/anankeCheckbox";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {AnankeIconButton} from "@vesta/ananke/components/anankeIconButton";
import {AnankeAccentStatusLozenge} from "@vesta/ananke/components/anankeLozenge";
import {AnankeTooltip} from "@vesta/ananke/components/anankeTooltip";
import {AnankeDropdownItem} from "@vesta/ananke/components/anankeDropdownItem";
import {AnankeDropdownItemGroup} from "@vesta/ananke/components/anankeDropdownItemGroup";
import {AnankeDropdownMenu} from "@vesta/ananke/components/anankeDropdownMenu";
import {CircularListFunnel} from "~/components/ottopilotDataViewer/circularListFunnel";
import type {ListFunnelProgress} from "~/components/ottopilotDataViewer/listFunnel";
import {
    inferListStatusPillType,
    listStatusPillTypeToLozengeStyle,
    type ListCardLozengeStyle,
    type ListCardStatus,
} from "~/components/ottopilotDataViewer/listStatusConfig";
import {twJoin} from "tailwind-merge";
import type {OttopilotListCardLayout} from "~/components/ottopilotDataViewer/view";

const LIST_CARD_LAYOUT_TRANSITION_CLASS =
    "transition-[padding,gap,grid-template-rows,max-height,opacity] duration-300 ease-in-out";

const listCardDesktopColumnCellClassName = "min-w-0 max-w-full w-full overflow-hidden";

const LIST_CARD_OU_CONTROL_WIDTH = "240px";
const listCardOuBadgeStyle = {
    position: "absolute" as const,
    right: 0,
    top: "39px",
    display: "flex",
    maxWidth: LIST_CARD_OU_CONTROL_WIDTH,
    padding: `${token("space.025")} ${token("space.075")}`,
    alignItems: "center",
    borderRadius: "3px",
    background: token("color.background.neutral.bold"),
    color: token("color.text.inverse"),
    fontFamily: "Inter",
    fontSize: "12px",
    lineHeight: "16px",
};

function displayOrDash(value: string | null | undefined): string {
    const trimmed = (value ?? "").trim();
    return trimmed === "" ? "-" : trimmed;
}

function listFooterIconStyle() {
    return {
        color: token("color.icon.brand"),
    };
}

const listDesktopColumnLabelStyle = {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: token("color.text.subtlest"),
    fontFeatureSettings: "'liga' off, 'calt' off",
    fontFamily: "Inter",
    fontSize: "var(--Font-Size-Body-S, 12px)",
    fontStyle: "normal",
    fontWeight: "var(--Font-Weight-Body-Medium, 500)",
    lineHeight: "var(--Line-Height-Body-S, 16px)",
} as const;

const listDesktopValueTextStyle = {
    color: token("color.text"),
    fontFamily: "Inter",
    fontSize: "14px",
    lineHeight: "20px",
} as const;

function ListCardEllipsisTooltip({
    text,
    className,
    style,
}: {
    text: string;
    className?: string;
    style?: CSSProperties;
}) {
    const normalized = text.trim();
    const trigger = (
        <span
            className={twJoin("block min-w-0 max-w-full truncate", className)}
            style={style}
        >
            {text}
        </span>
    );
    if (normalized === "" || normalized === "-") {
        return <div className={listCardDesktopColumnCellClassName}>{trigger}</div>;
    }
    return (
        <div className={listCardDesktopColumnCellClassName}>
            <AnankeTooltip content={text}>{trigger}</AnankeTooltip>
        </div>
    );
}

function ListDesktopColumnLabel({children}: {children: string}) {
    return (
        <ListCardEllipsisTooltip text={children} style={listDesktopColumnLabelStyle} />
    );
}

function ListDesktopTruncatedValue({
    text,
    className,
    style,
    noWrap = false,
}: {
    text: string;
    className?: string;
    style?: CSSProperties;
    noWrap?: boolean;
}) {
    const valueStyle = style ?? listDesktopValueTextStyle;

    if (noWrap === true) {
        const normalized = text.trim();
        const trigger = (
            <span
                className={twJoin("block min-w-0 max-w-full whitespace-nowrap", className)}
                style={valueStyle}
            >
                {text}
            </span>
        );
        if (normalized === "" || normalized === "-") {
            return <div className={listCardDesktopColumnCellClassName}>{trigger}</div>;
        }
        return (
            <div className={listCardDesktopColumnCellClassName}>
                <AnankeTooltip content={text}>{trigger}</AnankeTooltip>
            </div>
        );
    }

    return (
        <ListCardEllipsisTooltip
            text={text}
            className={className}
            style={valueStyle}
        />
    );
}

export type ListCardCategoryDisplay = {
    label: string;
    pillStyle: {
        color: string;
        backgroundColor: string;
        borderColor: string;
    };
};

const listCategoryPillTextStyle = {
    fontFamily: "Inter",
    fontFeatureSettings: "'liga' off, 'calt' off",
    fontSize: "var(--Font-Size-Body-S, 12px)",
    fontStyle: "normal",
    fontWeight: "var(--Font-Weight-Body-Regular, 400)",
    lineHeight: "var(--Line-Height-Body-S, 16px)",
    textAlign: "center" as const,
} as const;

function ListCardCategoryPillContent({categoryDisplay}: {categoryDisplay: ListCardCategoryDisplay}) {
    return (
        <AnankeTooltip content={categoryDisplay.label}>
            <span
                className="box-border inline-flex min-w-0 max-w-full items-center overflow-hidden"
                style={{
                    ...listCategoryPillTextStyle,
                    color: categoryDisplay.pillStyle.color,
                    backgroundColor: categoryDisplay.pillStyle.backgroundColor,
                    border: `0.5px solid ${categoryDisplay.pillStyle.borderColor}`,
                    borderRadius: "3px",
                    paddingInline: token("space.050"),
                    gap: token("space.025"),
                }}
            >
                <span className="truncate">{categoryDisplay.label}</span>
            </span>
        </AnankeTooltip>
    );
}

function ListCardCategoryPill({categoryDisplay}: {categoryDisplay: ListCardCategoryDisplay}) {
    return (
        <div className={listCardDesktopColumnCellClassName}>
            <ListCardCategoryPillContent categoryDisplay={categoryDisplay} />
        </div>
    );
}

const listCardMetaLineTextStyle = {
    color: token("color.text.subtle"),
    fontFamily: "Inter",
    fontSize: "var(--Font-Size-Body-S, 12px)",
    fontStyle: "normal",
    fontWeight: "var(--Font-Weight-Body-Medium, 500)",
    lineHeight: "var(--Line-Height-Body-S, 16px)",
} as const;

function ListCardMetaDotSeparator() {
    return (
        <span className="shrink-0" style={{color: token("color.text.subtle")}} aria-hidden="true">
            ·
        </span>
    );
}

function ListCardPhoneUtmMetaLine({
    phoneLabel,
    hasPhone,
    utmSourceLabel,
    hasUtmSource,
    onPhoneClick,
}: {
    phoneLabel: string;
    hasPhone: boolean;
    utmSourceLabel: string;
    hasUtmSource: boolean;
    onPhoneClick?: () => void;
}) {
    if (hasPhone !== true && hasUtmSource !== true) {
        return null;
    }

    return (
        <div className="flex min-w-0 max-w-full items-center gap-x-ds-space-075 overflow-hidden">
            {hasPhone === true ? (
                onPhoneClick !== undefined ? (
                    <button
                        type="button"
                        className="line-clamp-1 min-w-0 max-w-full truncate bg-transparent border-0 p-0 text-left"
                        style={listCardMetaLineTextStyle}
                        onClick={(event) => {
                            event.stopPropagation();
                            onPhoneClick();
                        }}
                    >
                        {phoneLabel}
                    </button>
                ) : (
                    <span className="line-clamp-1 min-w-0 truncate" style={listCardMetaLineTextStyle}>
                        {phoneLabel}
                    </span>
                )
            ) : null}
            {hasPhone === true && hasUtmSource === true ? <ListCardMetaDotSeparator /> : null}
            {hasUtmSource === true ? (
                <span className="line-clamp-1 min-w-0 flex-1 truncate" style={listCardMetaLineTextStyle}>
                    {utmSourceLabel}
                </span>
            ) : null}
        </div>
    );
}

function resolveTimelinessValueStyle(emphasis: ListCardTimelinessEmphasis | undefined): CSSProperties {
    if (emphasis === "overdue") {
        return {
            ...listDesktopValueTextStyle,
            color: token("color.text.accent.orange"),
            fontWeight: 600,
        };
    }
    if (emphasis === "due") {
        return {
            ...listDesktopValueTextStyle,
            color: token("color.text.accent.blue.bolder"),
            fontWeight: 600,
        };
    }
    return listDesktopValueTextStyle;
}

function ListCardDesktopDataRow({
    layout,
    listSelection,
    shouldShowFunnel,
    funnelProgress,
    leadingVisual,
    displayNameLabel,
    phoneLabel,
    hasPhone,
    utmSourceLabel,
    hasUtmSource,
    showPhoneUtmMetaLine,
    onPhoneClick,
    statusLabel,
    lozengeStyle,
    assignedOuColumnLabel,
    dealerNameLabel,
    assignedOuLozengeStyle,
    createdAtLabel,
    statusColumnLabel,
    timelinessColumnLabel,
    statusDisplayAsText,
    timelinessEmphasis,
    categoryDisplay,
    categoryColumnLabel = "Category",
    hideStatusColumn = false,
    shouldShowFooter,
    footerControls,
    footerActionsPreset,
    footerActionsRenderer,
    onScheduleClick,
    onDisposeClick,
    onCreateClick,
    onNotesClick,
    onEditClick,
    onLogCallClick,
    onProfileClick,
    onWhatsAppClick,
    whatsAppActionDisabled,
    footerActionsRestricted,
    scheduleClickDisabled,
    hideAssignedOuColumn,
}: {
    layout: Extract<OttopilotListCardLayout, "desktop" | "compact">;
    listSelection?: {
        isSelected: boolean;
        onToggleSelect: () => void;
    };
    shouldShowFunnel: boolean;
    funnelProgress?: ListFunnelProgress;
    leadingVisual?: ReactNode;
    displayNameLabel: string;
    phoneLabel: string;
    hasPhone: boolean;
    utmSourceLabel: string;
    hasUtmSource: boolean;
    showPhoneUtmMetaLine?: boolean;
    onPhoneClick?: () => void;
    statusLabel: string;
    lozengeStyle: ListCardLozengeStyle;
    assignedOuColumnLabel: string;
    dealerNameLabel: string;
    /** When set, assigned-OU / disposition column value renders as a lozenge (e.g. disposition tracker chips). */
    assignedOuLozengeStyle?: ListCardLozengeStyle | null;
    createdAtLabel: string;
    statusColumnLabel: string;
    timelinessColumnLabel: string;
    statusDisplayAsText?: boolean;
    timelinessEmphasis?: ListCardTimelinessEmphasis;
    categoryDisplay?: ListCardCategoryDisplay | null;
    categoryColumnLabel?: string;
    hideStatusColumn?: boolean;
    shouldShowFooter: boolean;
    footerControls: ListCardFooterControls | null;
    footerActionsPreset?: ListCardFooterActionsPreset;
    footerActionsRenderer?: () => ReactNode;
    onScheduleClick?: (journeyId: Uuid) => void;
    onDisposeClick?: (journeyId: Uuid) => void;
    onCreateClick?: (journeyId: Uuid) => void;
    onNotesClick?: (journeyId: Uuid) => void;
    onEditClick?: (journeyId: Uuid) => void;
    onLogCallClick?: (journeyId: Uuid) => void;
    onProfileClick?: (journeyId: Uuid) => void;
    onWhatsAppClick?: (journeyId: Uuid) => void;
    whatsAppActionDisabled?: boolean;
    footerActionsRestricted?: boolean;
    scheduleClickDisabled?: boolean;
    hideAssignedOuColumn?: boolean;
}) {
    const showInlineFooter = layout === "desktop" && shouldShowFooter && footerControls !== null;
    const showCategoryColumn = categoryDisplay !== null && categoryDisplay !== undefined;
    let columnIndex = 0;
    const checkboxColumn = listSelection !== undefined ? columnIndex++ : null;
    const funnelColumn = shouldShowFunnel ? columnIndex++ : null;
    const leadingVisualColumn = leadingVisual !== undefined ? columnIndex++ : null;
    const nameColumn = columnIndex++;
    const statusColumn = hideStatusColumn === true ? null : columnIndex++;
    const categoryColumn = showCategoryColumn === true ? columnIndex++ : null;
    if (statusColumn !== null || categoryColumn !== null) {
        columnIndex++; // spacer before disposition / due block
    }
    const dealerColumn = hideAssignedOuColumn === true ? null : columnIndex++;
    if (hideAssignedOuColumn === true) {
        columnIndex++; // placeholder for the hidden assigned OU column, preserving Dulux spacing.
    } else if (dealerColumn !== null) {
        columnIndex++; // gap: assigned OU ↔ created at
    }
    const createdAtColumn = columnIndex++;
    const footerColumn = showInlineFooter ? columnIndex : null;
    const spanTwoRowsStyle = {gridRow: "1 / span 2"} as const;

    const gridTemplateColumns = [
        listSelection !== undefined ? "max-content" : null,
        shouldShowFunnel ? "max-content" : null,
        leadingVisual !== undefined ? "max-content" : null,
        "minmax(0px, 1.25fr)",
        statusColumn !== null ? "max-content" : null,
        categoryColumn !== null ? "minmax(0px, 8rem)" : null,
        statusColumn !== null || categoryColumn !== null ? "minmax(0px, 0.5rem)" : null,
        hideAssignedOuColumn === true ? null : "minmax(0px, 1.5fr)",
        hideAssignedOuColumn === true ? null : "minmax(0px, 0.5rem)",
        hideAssignedOuColumn === true ? "minmax(0px, 1fr)" : "minmax(0px, 9rem)",
        showInlineFooter ? "max-content" : null,
    ]
        .filter((column) => column !== null)
        .join(" ");

    return (
        <div
            className={twJoin(
                "grid w-full min-w-0 max-w-full overflow-hidden items-center",
                LIST_CARD_LAYOUT_TRANSITION_CLASS,
                layout === "compact" ? "gap-x-ds-space-100 gap-y-ds-space-025" : "gap-x-ds-space-200 gap-y-ds-space-025",
            )}
            style={{
                gridTemplateColumns: gridTemplateColumns,
                gridTemplateRows: "auto auto",
            }}
        >
            {listSelection !== undefined && checkboxColumn !== null ? (
                <div
                    className="shrink-0 self-center"
                    style={{...spanTwoRowsStyle, gridColumn: checkboxColumn + 1}}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                >
                    <AnankeCheckbox
                        isChecked={listSelection.isSelected}
                        onChange={(event) => {
                            event.stopPropagation();
                            listSelection.onToggleSelect();
                        }}
                    />
                </div>
            ) : null}
            {shouldShowFunnel && funnelProgress !== undefined && funnelColumn !== null ? (
                <div className="self-center" style={{...spanTwoRowsStyle, gridColumn: funnelColumn + 1}}>
                    <CircularListFunnel progress={funnelProgress} />
                </div>
            ) : null}
            {leadingVisual !== undefined && leadingVisualColumn !== null ? (
                <div className="self-center" style={{...spanTwoRowsStyle, gridColumn: leadingVisualColumn + 1}}>
                    {leadingVisual}
                </div>
            ) : null}
            <div
                className={twJoin(listCardDesktopColumnCellClassName, "self-center")}
                style={{...spanTwoRowsStyle, gridColumn: nameColumn + 1}}
            >
                <div className="grid min-w-0 gap-y-ds-space-025">
                    <ListCardEllipsisTooltip
                        text={displayNameLabel}
                        style={{
                            color: token("color.text"),
                            fontFamily: "Inter",
                            fontSize: "var(--Font-Size-Body-M, 14px)",
                            fontWeight: "var(--Font-Weight-Body-Bold, 653)",
                            lineHeight: "var(--Line-Height-Body-M, 20px)",
                        }}
                    />
                    {showPhoneUtmMetaLine === true ? (
                        <ListCardPhoneUtmMetaLine
                            phoneLabel={phoneLabel}
                            hasPhone={hasPhone}
                            utmSourceLabel={utmSourceLabel}
                            hasUtmSource={hasUtmSource}
                            onPhoneClick={onPhoneClick}
                        />
                    ) : null}
                </div>
            </div>
            {statusColumn !== null ? (
                <div className={twJoin(listCardDesktopColumnCellClassName, "self-start")} style={{gridColumn: statusColumn + 1, gridRow: 1}}>
                    <ListDesktopColumnLabel>{statusColumnLabel}</ListDesktopColumnLabel>
                </div>
            ) : null}
            {categoryColumn !== null ? (
                <div className={twJoin(listCardDesktopColumnCellClassName, "self-start")} style={{gridColumn: categoryColumn + 1, gridRow: 1}}>
                    <ListDesktopColumnLabel>{categoryColumnLabel}</ListDesktopColumnLabel>
                </div>
            ) : null}
            {dealerColumn !== null ? (
                <div className={twJoin(listCardDesktopColumnCellClassName, "self-start")} style={{gridColumn: dealerColumn + 1, gridRow: 1}}>
                    <ListDesktopColumnLabel>{assignedOuColumnLabel}</ListDesktopColumnLabel>
                </div>
            ) : null}
            <div className={twJoin(listCardDesktopColumnCellClassName, "self-start")} style={{gridColumn: createdAtColumn + 1, gridRow: 1}}>
                <ListDesktopColumnLabel>{timelinessColumnLabel}</ListDesktopColumnLabel>
            </div>
            {statusColumn !== null ? (
                <div className={twJoin(listCardDesktopColumnCellClassName, "self-start")} style={{gridColumn: statusColumn + 1, gridRow: 2}}>
                    {statusDisplayAsText === true ? (
                        <ListDesktopTruncatedValue text={statusLabel} />
                    ) : (
                        <AnankeTooltip content={statusLabel}>
                            <span className="inline-flex min-w-0 max-w-full overflow-hidden">
                                <AnankeAccentStatusLozenge style={lozengeStyle} maxWidth="100%">
                                    {statusLabel}
                                </AnankeAccentStatusLozenge>
                            </span>
                        </AnankeTooltip>
                    )}
                </div>
            ) : null}
            {categoryColumn !== null && categoryDisplay !== null && categoryDisplay !== undefined ? (
                <div className={twJoin(listCardDesktopColumnCellClassName, "self-start")} style={{gridColumn: categoryColumn + 1, gridRow: 2}}>
                    <ListCardCategoryPill categoryDisplay={categoryDisplay} />
                </div>
            ) : null}
            {dealerColumn !== null ? (
                <div className={twJoin(listCardDesktopColumnCellClassName, "self-start")} style={{gridColumn: dealerColumn + 1, gridRow: 2}}>
                    {assignedOuLozengeStyle !== null
                    && assignedOuLozengeStyle !== undefined
                    && dealerNameLabel.trim() !== ""
                    && dealerNameLabel !== "-" ? (
                        <AnankeTooltip content={dealerNameLabel}>
                            <span className="inline-flex min-w-0 max-w-full overflow-hidden">
                                <AnankeAccentStatusLozenge style={assignedOuLozengeStyle} maxWidth="100%" formatLabel={false}>
                                    {dealerNameLabel}
                                </AnankeAccentStatusLozenge>
                            </span>
                        </AnankeTooltip>
                    ) : (
                        <ListDesktopTruncatedValue text={dealerNameLabel} />
                    )}
                </div>
            ) : null}
            <div className={twJoin(listCardDesktopColumnCellClassName, "self-start")} style={{gridColumn: createdAtColumn + 1, gridRow: 2}}>
                <ListDesktopTruncatedValue
                    text={createdAtLabel}
                    noWrap={timelinessEmphasis === "due" || timelinessEmphasis === "overdue"}
                    style={resolveTimelinessValueStyle(timelinessEmphasis)}
                />
            </div>
            {showInlineFooter && footerColumn !== null ? (
                <div
                    className="min-w-0 shrink-0 self-center justify-self-end overflow-visible"
                    style={{...spanTwoRowsStyle, gridColumn: footerColumn + 1}}
                >
                    {footerActionsRenderer !== undefined ? (
                        footerActionsRenderer()
                    ) : (
                        <ListCardFooterActions
                            footerControls={footerControls}
                            layout="desktop"
                            footerActionsPreset={footerActionsPreset}
                            footerActionsRestricted={footerActionsRestricted}
                            scheduleClickDisabled={scheduleClickDisabled}
                            whatsAppActionDisabled={whatsAppActionDisabled}
                            onScheduleClick={onScheduleClick}
                            onDisposeClick={onDisposeClick}
                            onCreateClick={onCreateClick}
                            onNotesClick={onNotesClick}
                            onEditClick={onEditClick}
                            onLogCallClick={onLogCallClick}
                            onProfileClick={onProfileClick}
                            onWhatsAppClick={onWhatsAppClick}
                        />
                    )}
                </div>
            ) : null}
        </div>
    );
}

function ListCardFooterActions({
    footerControls,
    layout,
    footerActionsPreset = "default",
    footerActionsRestricted,
    scheduleClickDisabled,
    whatsAppActionDisabled,
    onScheduleClick,
    onDisposeClick,
    onCreateClick,
    onNotesClick,
    onEditClick,
    onLogCallClick,
    onProfileClick,
    onWhatsAppClick,
}: {
    footerControls: ListCardFooterControls;
    layout: "mobile" | "desktop";
    footerActionsPreset?: ListCardFooterActionsPreset;
    footerActionsRestricted?: boolean;
    scheduleClickDisabled?: boolean;
    whatsAppActionDisabled?: boolean;
    onScheduleClick?: (journeyId: Uuid) => void;
    onDisposeClick?: (journeyId: Uuid) => void;
    onCreateClick?: (journeyId: Uuid) => void;
    onNotesClick?: (journeyId: Uuid) => void;
    onEditClick?: (journeyId: Uuid) => void;
    onLogCallClick?: (journeyId: Uuid) => void;
    onProfileClick?: (journeyId: Uuid) => void;
    onWhatsAppClick?: (journeyId: Uuid) => void;
}) {
    const iconColor =
        footerActionsRestricted === true
            ? token("color.icon.disabled")
            : listFooterIconStyle().color;
    const [moreMenuOpen, setMoreMenuOpen] = useState(false);

    const defaultActions = (
        <>
            <FooterAction
                icon={<ScheduleCallIcon color={iconColor} />}
                label="Schedule"
                isDisabled={footerActionsRestricted === true || scheduleClickDisabled === true}
                onClick={() => onScheduleClick?.(footerControls.journeyId)}
            />
            <FooterAction
                icon={<RoadmapIcon label="" size="small" color={iconColor} />}
                label="Dispose"
                isDisabled={footerActionsRestricted}
                onClick={() => onDisposeClick?.(footerControls.journeyId)}
            />
            {onLogCallClick !== undefined ? (
                <FooterAction
                    icon={<OnCallIcon label="" color={iconColor} />}
                    label="Log a call"
                    isDisabled={footerActionsRestricted}
                    onClick={() => onLogCallClick(footerControls.journeyId)}
                />
            ) : (
                <FooterAction
                    icon={<AddIcon label="" size="small" color={iconColor} />}
                    label="Create"
                    isDisabled={footerActionsRestricted}
                    onClick={() => onCreateClick?.(footerControls.journeyId)}
                />
            )}
            <FooterAction
                icon={<CommentIcon label="" size="small" color={iconColor} />}
                label="Notes"
                isDisabled={footerActionsRestricted}
                onClick={() => onNotesClick?.(footerControls.journeyId)}
            />
        </>
    );

    const menuIconColor =
        footerActionsRestricted === true
            ? token("color.icon.disabled")
            : token("color.icon");
    const scheduleMenuIconColor =
        footerActionsRestricted === true || scheduleClickDisabled === true
            ? token("color.icon.disabled")
            : token("color.icon");

    const dayPlanMoreMenu = (
        <AnankeDropdownMenu
            isOpen={moreMenuOpen}
            onOpenChange={({isOpen}) => {
                setMoreMenuOpen(isOpen);
            }}
            placement="bottom-end"
            shouldRenderToParent={false}
            trigger={({triggerRef, ...triggerProps}) => (
                <button
                    type="button"
                    ref={triggerRef as Ref<HTMLButtonElement>}
                    {...triggerProps}
                    disabled={footerActionsRestricted === true}
                    onClick={(event) => {
                        event.stopPropagation();
                        triggerProps.onClick?.(event);
                    }}
                    onPointerDown={(event) => {
                        event.stopPropagation();
                    }}
                    className="grid grid-rows-[max-content_max-content] justify-items-center gap-y-ds-space-050 bg-transparent border-0 p-0 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    <span className="grid h-6 w-6 place-items-center [&_svg]:h-6 [&_svg]:w-6">
                        <ShowMoreHorizontalIcon label="" size="small" color={iconColor} />
                    </span>
                    <span
                        style={{
                            color: token("color.text.subtle"),
                            fontFamily: "Inter",
                            fontSize: "var(--Font-Size-Body-S, 12px)",
                            fontStyle: "normal",
                            fontWeight: "var(--Font-Weight-Body-Regular, 400)",
                            lineHeight: "var(--Line-Height-Body-S, 16px)",
                        }}
                    >
                        More
                    </span>
                </button>
            )}
        >
            <AnankeDropdownItemGroup>
                <AnankeDropdownItem
                    elemBefore={<RoadmapIcon label="" size="small" color={menuIconColor} />}
                    onClick={(event) => {
                        event.stopPropagation();
                        setMoreMenuOpen(false);
                        onDisposeClick?.(footerControls.journeyId);
                    }}
                >
                    Dispose
                </AnankeDropdownItem>
                {whatsAppActionDisabled !== true ? (
                    <AnankeDropdownItem
                        elemBefore={<WhatsappIcon />}
                        onClick={(event) => {
                            event.stopPropagation();
                            setMoreMenuOpen(false);
                            onWhatsAppClick?.(footerControls.journeyId);
                        }}
                    >
                        WhatsApp
                    </AnankeDropdownItem>
                ) : null}
            </AnankeDropdownItemGroup>
        </AnankeDropdownMenu>
    );

    const dayPlanActions = (
        <>
            <FooterAction
                icon={<OnCallIcon label="" color={iconColor} />}
                label="Log a call"
                isDisabled={footerActionsRestricted}
                onClick={() => onLogCallClick?.(footerControls.journeyId)}
            />
            <FooterAction
                icon={<CommentIcon label="" size="small" color={iconColor} />}
                label="Notes"
                isDisabled={footerActionsRestricted}
                onClick={() => onNotesClick?.(footerControls.journeyId)}
            />
            <FooterAction
                icon={<ScheduleCallIcon color={iconColor} />}
                label="Schedule a call"
                isDisabled={footerActionsRestricted === true || scheduleClickDisabled === true}
                onClick={() => onScheduleClick?.(footerControls.journeyId)}
            />
            {dayPlanMoreMenu}
        </>
    );

    const yourQueueMoreMenu = (
        <AnankeDropdownMenu
            isOpen={moreMenuOpen}
            onOpenChange={({isOpen}) => {
                setMoreMenuOpen(isOpen);
            }}
            placement="bottom-end"
            shouldRenderToParent={false}
            trigger={({triggerRef, ...triggerProps}) => (
                <button
                    type="button"
                    ref={triggerRef as Ref<HTMLButtonElement>}
                    {...triggerProps}
                    disabled={footerActionsRestricted === true}
                    onClick={(event) => {
                        event.stopPropagation();
                        triggerProps.onClick?.(event);
                    }}
                    onPointerDown={(event) => {
                        event.stopPropagation();
                    }}
                    className="grid grid-rows-[max-content_max-content] justify-items-center gap-y-ds-space-050 bg-transparent border-0 p-0 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    <span className="grid h-6 w-6 place-items-center [&_svg]:h-6 [&_svg]:w-6">
                        <ShowMoreHorizontalIcon label="" size="small" color={iconColor} />
                    </span>
                    <span
                        style={{
                            color: token("color.text.subtle"),
                            fontFamily: "Inter",
                            fontSize: "var(--Font-Size-Body-S, 12px)",
                            fontStyle: "normal",
                            fontWeight: "var(--Font-Weight-Body-Regular, 400)",
                            lineHeight: "var(--Line-Height-Body-S, 16px)",
                        }}
                    >
                        More
                    </span>
                </button>
            )}
        >
            <AnankeDropdownItemGroup>
                <AnankeDropdownItem
                    elemBefore={<RoadmapIcon label="" size="small" color={menuIconColor} />}
                    onClick={(event) => {
                        event.stopPropagation();
                        setMoreMenuOpen(false);
                        onDisposeClick?.(footerControls.journeyId);
                    }}
                >
                    Dispose
                </AnankeDropdownItem>
                <AnankeDropdownItem
                    isDisabled={footerActionsRestricted === true || scheduleClickDisabled === true}
                    elemBefore={<ScheduleCallIcon size={20} color={scheduleMenuIconColor} />}
                    onClick={(event) => {
                        event.stopPropagation();
                        setMoreMenuOpen(false);
                        onScheduleClick?.(footerControls.journeyId);
                    }}
                >
                    Schedule a call
                </AnankeDropdownItem>
                {whatsAppActionDisabled !== true ? (
                    <AnankeDropdownItem
                        elemBefore={<WhatsappIcon />}
                        onClick={(event) => {
                            event.stopPropagation();
                            setMoreMenuOpen(false);
                            onWhatsAppClick?.(footerControls.journeyId);
                        }}
                    >
                        WhatsApp
                    </AnankeDropdownItem>
                ) : null}
            </AnankeDropdownItemGroup>
        </AnankeDropdownMenu>
    );

    const yourQueueActions = (
        <>
            <FooterAction
                icon={<LinkExternalIcon label="" size="small" color={iconColor} />}
                label="Profile"
                isDisabled={footerActionsRestricted}
                onClick={() => onProfileClick?.(footerControls.journeyId)}
            />
            <FooterAction
                icon={<OnCallIcon label="" color={iconColor} />}
                label="Log a call"
                isDisabled={footerActionsRestricted}
                onClick={() => onLogCallClick?.(footerControls.journeyId)}
            />
            <FooterAction
                icon={<CommentIcon label="" size="small" color={iconColor} />}
                label="Notes"
                isDisabled={footerActionsRestricted}
                onClick={() => onNotesClick?.(footerControls.journeyId)}
            />
            {yourQueueMoreMenu}
        </>
    );

    const actions =
        footerActionsPreset === "yourQueue"
            ? yourQueueActions
            : footerActionsPreset === "dayPlan"
              ? dayPlanActions
              : defaultActions;

    if (layout === "desktop") {
        return (
            <div
                className="grid min-w-0 w-full grid-flow-col auto-cols-max items-center justify-end justify-self-end gap-x-ds-space-150"
                data-list-card-footer=""
                onClick={(event) => {
                    event.stopPropagation();
                }}
                onPointerDown={(event) => {
                    event.stopPropagation();
                }}
            >
                {actions}
            </div>
        );
    }

    return (
        <>
            <div className="h-px" style={{background: token("color.border")}} />
            <div
                className="grid grid-cols-4 justify-items-center"
                data-list-card-footer=""
                onClick={(event) => {
                    event.stopPropagation();
                }}
                onPointerDown={(event) => {
                    event.stopPropagation();
                }}
            >
                {actions}
            </div>
        </>
    );
}

export type {ListCardLozengeStyle, ListCardStatus, ListStatusPillType} from "~/components/ottopilotDataViewer/listStatusConfig";

export type ListCardFooterActionsPreset = "default" | "dayPlan" | "yourQueue";
export type ListCardTimelinessEmphasis = "default" | "overdue" | "due";

export type ListCardFooterControls = {
    selectedDispositionId: Uuid | null;
    setSelectedDispositoinId: Dispatch<SetStateAction<Uuid | null>>;
    selectedRemarks: string | null;
    setSelectedRemarks: Dispatch<SetStateAction<string | null>>;
    selectedSelectedNotes: string | null;
    setSelectedSelectedNotes: Dispatch<SetStateAction<string | null>>;
    journeyId: Uuid;
};

function FooterAction({
    icon,
    label,
    isDisabled,
    onClick,
}: {
    icon: ReactNode;
    label: string;
    isDisabled?: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            disabled={isDisabled === true}
            onClick={(event) => {
                event.stopPropagation();
                if (isDisabled === true) {
                    return;
                }
                onClick();
            }}
            className="grid grid-rows-[max-content_max-content] justify-items-center gap-y-ds-space-050 bg-transparent border-0 p-0 disabled:cursor-not-allowed disabled:opacity-50"
        >
            <span className="grid h-6 w-6 place-items-center [&_svg]:h-6 [&_svg]:w-6">{icon}</span>
            <span
                style={{
                    color: token("color.text.subtle"),
                    fontFamily: "Inter",
                    fontSize: "var(--Font-Size-Body-S, 12px)",
                    fontStyle: "normal",
                    fontWeight: "var(--Font-Weight-Body-Regular, 400)",
                    lineHeight: "var(--Line-Height-Body-S, 16px)",
                }}
            >
                {label}
            </span>
        </button>
    );
}

const DEFAULT_ASSIGNED_OU_COLUMN_LABEL = "Dealer Name";
const DEFAULT_STATUS_COLUMN_LABEL = "Status";
const DEFAULT_TIMELINESS_COLUMN_LABEL = "Created at";

export function ListCardComponent({
    diplayName,
    phoneNumber,
    status,
    createdAt,
    assignedOuName,
    assignedOuColumnLabel = DEFAULT_ASSIGNED_OU_COLUMN_LABEL,
    hideAssignedOuColumn = false,
    statusColumnLabel = DEFAULT_STATUS_COLUMN_LABEL,
    timelinessColumnLabel = DEFAULT_TIMELINESS_COLUMN_LABEL,
    timelinessDisplayValue,
    mobileTimelinessDisplayValue,
    hidePhoneMetaLine = false,
    timelinessEmphasis,
    statusDisplayAsText,
    funnelProgress,
    leadingVisual,
    layout = "mobile",
    listSelection,
    footerControls,
    footerActionsPreset = "default",
    footerActionsRenderer,
    onPhoneClick,
    onCardClick,
    onScheduleClick,
    onDisposeClick,
    onCreateClick,
    onNotesClick,
    onEditClick,
    onLogCallClick,
    onProfileClick,
    onWhatsAppClick,
    whatsAppActionDisabled,
    footerActionsRestricted,
    scheduleClickDisabled,
    utmSource,
    categoryDisplay = null,
    categoryColumnLabel = "Category",
    hideStatusColumn = false,
    assignedOuLozengeStyle = null,
}: {
    diplayName: string;
    phoneNumber: string | null;
    status: ListCardStatus;
    createdAt: Iso8601DateTime;
    assignedOuName: string | null;
    /** Desktop/compact column header for the assigned OU value (default: Dealer Name). */
    assignedOuColumnLabel?: string;
    statusColumnLabel?: string;
    timelinessColumnLabel?: string;
    /** When set, replaces formatted `createdAt` in desktop/mobile timeliness column. */
    timelinessDisplayValue?: string;
    /** When set, used on mobile layout instead of {@link timelinessDisplayValue}. */
    mobileTimelinessDisplayValue?: string;
    hidePhoneMetaLine?: boolean;
    timelinessEmphasis?: ListCardTimelinessEmphasis;
    /** When true, disposition-style values render as plain text instead of a lozenge. */
    statusDisplayAsText?: boolean;
    /** Journey event-count funnel; when set, shown at the top-left of the card. */
    funnelProgress?: ListFunnelProgress;
    /** Optional media/icon visual shown before the primary name block. */
    leadingVisual?: ReactNode;
    layout?: OttopilotListCardLayout;
    listSelection?: {
        isSelected: boolean;
        onToggleSelect: () => void;
    };
    footerControls: ListCardFooterControls | null;
    footerActionsPreset?: ListCardFooterActionsPreset;
    footerActionsRenderer?: () => ReactNode;
    onPhoneClick?: () => void;
    /** Opens the data viewer read panel (card body); footer actions call `stopPropagation`. */
    onCardClick?: () => void;
    onScheduleClick?: (journeyId: Uuid) => void;
    onDisposeClick?: (journeyId: Uuid) => void;
    onCreateClick?: (journeyId: Uuid) => void;
    onNotesClick?: (journeyId: Uuid) => void;
    onEditClick?: (journeyId: Uuid) => void;
    onLogCallClick?: (journeyId: Uuid) => void;
    onProfileClick?: (journeyId: Uuid) => void;
    onWhatsAppClick?: (journeyId: Uuid) => void;
    whatsAppActionDisabled?: boolean;
    footerActionsRestricted?: boolean;
    scheduleClickDisabled?: boolean;
    /** When true, hides the assigned OU / State column on desktop list cards. */
    hideAssignedOuColumn?: boolean;
    /** When set, shows phone and UTM source under the name (desktop) or as a combined meta line (mobile). */
    utmSource?: string | null;
    categoryDisplay?: ListCardCategoryDisplay | null;
    categoryColumnLabel?: string;
    hideStatusColumn?: boolean;
    /** When set, assigned-OU / disposition column value renders as a lozenge. */
    assignedOuLozengeStyle?: ListCardLozengeStyle | null;
}) {
    const displayNameLabel = displayOrDash(diplayName);
    const safePhone = phoneNumber?.trim() ?? "";
    const hasPhone = safePhone !== "";
    const phoneLabel = hasPhone ? safePhone : "-";
    const showPhoneUtmMetaLine = utmSource !== undefined;
    const hasUtmSource = showPhoneUtmMetaLine && (utmSource ?? "").trim() !== "";
    const utmSourceLabel = hasUtmSource ? (utmSource ?? "").trim() : "-";
    const statusLabel = displayOrDash(status.name);
    const hasFooterActionsRenderer = footerActionsRenderer !== undefined;
    const hasFooterControls = footerControls !== null;
    const shouldShowFooter = hasFooterControls || hasFooterActionsRenderer;
    /** Row-actions-only footer (no disposition controls): stacked bar under the card, matching profile Figma. */
    const useStackedRowActionsFooter = hasFooterActionsRenderer && hasFooterControls !== true;
    const shouldShowOu =
        hideAssignedOuColumn !== true && assignedOuName !== null && assignedOuName.trim() !== "";
    const isMobile = useCheckMobile();
    const [ouHovered, setOuHovered] = useState(false);
    const [ouPinnedOnMobile, setOuPinnedOnMobile] = useState(false);
    const isOuVisible = shouldShowOu && (isMobile ? ouPinnedOnMobile : ouHovered);
    const lozengeStyle: ListCardLozengeStyle =
        status.lozengeStyle
        ?? (status.pillType !== undefined
            ? listStatusPillTypeToLozengeStyle(status.pillType)
            : listStatusPillTypeToLozengeStyle(inferListStatusPillType(status.name)));
    const shouldShowFunnel = funnelProgress !== undefined && funnelProgress.total > 0;
    const isDesktopLayout = layout === "desktop";
    const isCompactLayout = layout === "compact";
    const isMobileLayout = layout === "mobile";
    const usesDesktopStyleRow = isDesktopLayout || isCompactLayout;
    const dealerNameLabel = displayOrDash(assignedOuName);
    const formattedCreatedAt = usesDesktopStyleRow ? formatDate(createdAt) : formatDateTime(createdAt);
    const createdAtLabel = timelinessDisplayValue ?? formattedCreatedAt;
    const mobileTimelinessLabel = mobileTimelinessDisplayValue ?? createdAtLabel;
    const showStackedFooter =
        ((isCompactLayout || isMobileLayout) && shouldShowFooter)
        || useStackedRowActionsFooter === true;
    const isListCardSelected = listSelection?.isSelected === true;
    const isListCardInteractive = onCardClick !== undefined;

    // Stacked row-actions footer: no card bottom padding — the action bar owns compact vertical space.
    const cardPaddingClass =
        useStackedRowActionsFooter === true
            ? usesDesktopStyleRow
                ? "px-ds-space-200 pt-ds-space-200 pb-0"
                : "px-ds-space-100 pt-ds-space-100 pb-0"
            : usesDesktopStyleRow
              ? "p-ds-space-200"
              : "p-ds-space-100";
    const stackedFooterBleedClass = usesDesktopStyleRow ? "-mx-ds-space-200" : "-mx-ds-space-100";
    return (
        <div
            className={twJoin(
                "grid w-full min-w-0 max-w-full overflow-hidden rounded-[4px] border transition-colors duration-small",
                LIST_CARD_LAYOUT_TRANSITION_CLASS,
                "gap-y-ds-space-100",
                cardPaddingClass,
                isListCardSelected ? "bg-ds-background-selected" : "bg-ds-background-input",
                isListCardInteractive === true
                    && isListCardSelected !== true
                    && "hover:bg-ds-background-neutral-subtle-hovered",
                isListCardInteractive === true && "cursor-pointer",
            )}
            style={{
                borderColor: token("color.border"),
                boxShadow: "0 0 1px 0 rgba(30, 31, 33, 0.31), 0 8px 12px 0 rgba(30, 31, 33, 0.15)",
            }}
            onClick={(event) => {
                event.stopPropagation();
                const target = event.target;
                if (target instanceof Element && target.closest("[data-list-card-footer]") !== null) {
                    return;
                }
                onCardClick?.();
            }}
            onKeyDown={
                onCardClick === undefined
                    ? undefined
                    : (event) => {
                          if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              event.stopPropagation();
                              onCardClick();
                          }
                      }
            }
            role={onCardClick === undefined ? undefined : "button"}
            tabIndex={onCardClick === undefined ? undefined : 0}
        >
            {usesDesktopStyleRow ? (
                <>
                    <ListCardDesktopDataRow
                        layout={isCompactLayout ? "compact" : "desktop"}
                        listSelection={listSelection}
                        shouldShowFunnel={shouldShowFunnel}
                        funnelProgress={funnelProgress}
                        leadingVisual={leadingVisual}
                        displayNameLabel={displayNameLabel}
                        phoneLabel={phoneLabel}
                        hasPhone={hasPhone}
                        utmSourceLabel={utmSourceLabel}
                        hasUtmSource={hasUtmSource}
                        showPhoneUtmMetaLine={showPhoneUtmMetaLine}
                        onPhoneClick={onPhoneClick}
                        statusLabel={statusLabel}
                        lozengeStyle={lozengeStyle}
                        assignedOuColumnLabel={assignedOuColumnLabel}
                        dealerNameLabel={dealerNameLabel}
                        assignedOuLozengeStyle={assignedOuLozengeStyle}
                        createdAtLabel={createdAtLabel}
                        hideAssignedOuColumn={hideAssignedOuColumn}
                        statusColumnLabel={statusColumnLabel}
                        timelinessColumnLabel={timelinessColumnLabel}
                        statusDisplayAsText={statusDisplayAsText}
                        timelinessEmphasis={timelinessEmphasis}
                        categoryDisplay={categoryDisplay}
                        categoryColumnLabel={categoryColumnLabel}
                        hideStatusColumn={hideStatusColumn}
                        shouldShowFooter={shouldShowFooter}
                        footerControls={footerControls}
                        footerActionsPreset={footerActionsPreset}
                        footerActionsRenderer={footerActionsRenderer}
                        footerActionsRestricted={footerActionsRestricted}
                        scheduleClickDisabled={scheduleClickDisabled}
                        whatsAppActionDisabled={whatsAppActionDisabled}
                        onScheduleClick={onScheduleClick}
                        onDisposeClick={onDisposeClick}
                        onCreateClick={onCreateClick}
                        onNotesClick={onNotesClick}
                        onEditClick={onEditClick}
                        onLogCallClick={onLogCallClick}
                        onProfileClick={onProfileClick}
                        onWhatsAppClick={onWhatsAppClick}
                    />
                    {useStackedRowActionsFooter === true && footerActionsRenderer !== undefined ? (
                        <div className={twJoin("grid", stackedFooterBleedClass)}>
                            {footerActionsRenderer()}
                        </div>
                    ) : (
                        <div
                            className={twJoin(
                                "grid overflow-hidden",
                                LIST_CARD_LAYOUT_TRANSITION_CLASS,
                                showStackedFooter === true
                                    ? "max-h-22 opacity-100"
                                    : "max-h-0 opacity-0 pointer-events-none",
                            )}
                            aria-hidden={showStackedFooter !== true}
                        >
                            {showStackedFooter === true ? (
                                footerActionsRenderer !== undefined ? (
                                    <div className="px-ds-space-100 py-ds-space-100">{footerActionsRenderer()}</div>
                                ) : footerControls !== null ? (
                                    <ListCardFooterActions
                                        footerControls={footerControls}
                                        layout="mobile"
                                        footerActionsPreset={footerActionsPreset}
                                        footerActionsRestricted={footerActionsRestricted}
                                        scheduleClickDisabled={scheduleClickDisabled}
                                        whatsAppActionDisabled={whatsAppActionDisabled}
                                        onScheduleClick={onScheduleClick}
                                        onDisposeClick={onDisposeClick}
                                        onCreateClick={onCreateClick}
                                        onNotesClick={onNotesClick}
                                        onEditClick={onEditClick}
                                        onLogCallClick={onLogCallClick}
                                        onProfileClick={onProfileClick}
                                        onWhatsAppClick={onWhatsAppClick}
                                    />
                                ) : null
                            ) : null}
                        </div>
                    )}
                </>
            ) : (
            <div
                className="grid items-start gap-x-ds-space-100"
                style={{
                    gridTemplateColumns:
                        shouldShowFunnel === true && leadingVisual !== undefined
                            ? "max-content max-content minmax(0, 1fr)"
                            : shouldShowFunnel === true
                                ? "max-content minmax(0, 1fr)"
                                : leadingVisual !== undefined
                                    ? "max-content minmax(0, 1fr)"
                                    : "minmax(0, 1fr)",
                }}
            >
                {shouldShowFunnel ? <CircularListFunnel progress={funnelProgress} /> : null}
                {leadingVisual !== undefined ? <div className="self-start">{leadingVisual}</div> : null}
                <div className="relative min-w-0">
                    <div
                        className="grid gap-y-ds-space-075"
                        style={shouldShowOu ? {paddingRight: token("space.500")} : undefined}
                    >
                        <div
                            className="line-clamp-1"
                            style={{
                                color: token("color.text"),
                                fontFamily: "Inter",
                                fontSize: "var(--Font-Size-Body-M, 14px)",
                                fontStyle: "normal",
                                fontWeight: "var(--Font-Weight-Body-Bold, 653)",
                                lineHeight: "var(--Line-Height-Body-M, 20px)",
                            }}
                        >
                            {displayNameLabel}
                        </div>
                        {hidePhoneMetaLine === true ? null : showPhoneUtmMetaLine === true ? (
                            <ListCardPhoneUtmMetaLine
                                phoneLabel={phoneLabel}
                                hasPhone={hasPhone}
                                utmSourceLabel={utmSourceLabel}
                                hasUtmSource={hasUtmSource}
                                onPhoneClick={onPhoneClick}
                            />
                        ) : hasPhone === true ? (
                            <a
                                href={`tel:${safePhone.replace(/\s+/g, "")}`}
                                className="line-clamp-1 inline-block max-w-full w-fit"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    event.preventDefault();
                                    onPhoneClick?.();
                                }}
                                style={{
                                    color: token("color.text.selected"),
                                    fontFamily: "Inter",
                                    fontSize: "var(--Font-Size-Body-S, 12px)",
                                    fontStyle: "normal",
                                    fontWeight: "var(--Font-Weight-Body-Medium, 500)",
                                    lineHeight: "var(--Line-Height-Body-S, 16px)",
                                    textDecoration: "none",
                                    cursor: "pointer",
                                }}
                            >
                                {safePhone}
                            </a>
                        ) : (
                            <span
                                className="line-clamp-1 inline-block"
                                style={{
                                    color: token("color.text.subtle"),
                                    fontFamily: "Inter",
                                    fontSize: "var(--Font-Size-Body-S, 12px)",
                                    fontStyle: "normal",
                                    fontWeight: "var(--Font-Weight-Body-Medium, 500)",
                                    lineHeight: "var(--Line-Height-Body-S, 16px)",
                                }}
                            >
                                {phoneLabel}
                            </span>
                        )}
                        <div className="grid grid-cols-[max-content_max-content_max-content] items-center gap-x-ds-space-075 min-w-0">
                            {categoryDisplay !== null && categoryDisplay !== undefined ? (
                                <ListCardCategoryPillContent categoryDisplay={categoryDisplay} />
                            ) : statusDisplayAsText === true ? (
                                <span
                                    className="line-clamp-1 min-w-0 max-w-full"
                                    style={{
                                        color: token("color.text"),
                                        fontFamily: "Inter",
                                        fontSize: "var(--Font-Size-Body-S, 12px)",
                                        fontWeight: 500,
                                        lineHeight: "var(--Line-Height-Body-S, 16px)",
                                    }}
                                >
                                    {statusLabel}
                                </span>
                            ) : (
                                <AnankeAccentStatusLozenge style={lozengeStyle}>{statusLabel}</AnankeAccentStatusLozenge>
                            )}
                            <span style={{color: token("color.text.subtle")}}>·</span>
                            <span
                                className="line-clamp-1 min-w-0"
                                style={{
                                    ...(timelinessEmphasis === "overdue"
                                        ? {color: token("color.text.accent.orange"), fontWeight: 600}
                                        : timelinessEmphasis === "due"
                                          ? {color: token("color.text.accent.blue.bolder"), fontWeight: 600}
                                          : {color: token("color.text.subtle")}),
                                    fontFamily: "Inter",
                                    fontSize: "var(--Font-Size-Body-M, 14px)",
                                    fontStyle: "normal",
                                    fontWeight:
                                        timelinessEmphasis === "overdue" || timelinessEmphasis === "due"
                                            ? 600
                                            : 400,
                                    lineHeight: "var(--Line-Height-Body-M, 20px)",
                                }}
                            >
                                {mobileTimelinessLabel}
                            </span>
                        </div>
                    </div>
                    {shouldShowOu ? (
                        <>
                            <div
                                className="absolute z-10"
                                style={{right: "10px", top: 0}}
                                onPointerDown={(event: PointerEvent<HTMLDivElement>) => {
                                    event.stopPropagation();
                                }}
                                onMouseEnter={() => {
                                    if (isMobile !== true) {
                                        setOuHovered(true);
                                    }
                                }}
                                onMouseLeave={() => {
                                    if (isMobile !== true) {
                                        setOuHovered(false);
                                    }
                                }}
                            >
                                <AnankeIconButton
                                    type="button"
                                    appearance="subtle"
                                    spacing="compact"
                                    icon={SplitParallelIcon}
                                    label={assignedOuName ?? "Organization"}
                                    isSelected={isOuVisible}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        if (isMobile === true) {
                                            setOuPinnedOnMobile((prev) => !prev);
                                        }
                                    }}
                                />
                            </div>
                            <span
                                className="line-clamp-2 z-10"
                                style={{
                                    ...listCardOuBadgeStyle,
                                    right: "10px",
                                    visibility: isOuVisible ? "visible" : "hidden",
                                    pointerEvents: "none",
                                }}
                                aria-hidden={isOuVisible !== true}
                            >
                                {assignedOuName}
                            </span>
                        </>
                    ) : null}
                </div>
            </div>
            )}

            {showStackedFooter === true && isMobileLayout === true ? (
                footerActionsRenderer !== undefined ? (
                    useStackedRowActionsFooter === true ? (
                        <div className={twJoin("grid", stackedFooterBleedClass)}>
                            {footerActionsRenderer()}
                        </div>
                    ) : (
                        footerActionsRenderer()
                    )
                ) : footerControls !== null ? (
                    <ListCardFooterActions
                        footerControls={footerControls}
                        layout="mobile"
                        footerActionsPreset={footerActionsPreset}
                        footerActionsRestricted={footerActionsRestricted}
                        scheduleClickDisabled={scheduleClickDisabled}
                        whatsAppActionDisabled={whatsAppActionDisabled}
                        onScheduleClick={onScheduleClick}
                        onDisposeClick={onDisposeClick}
                        onCreateClick={onCreateClick}
                        onNotesClick={onNotesClick}
                        onEditClick={onEditClick}
                        onLogCallClick={onLogCallClick}
                        onProfileClick={onProfileClick}
                        onWhatsAppClick={onWhatsAppClick}
                    />
                ) : null
            ) : null}
        </div>
    );
}
